// Chaque clip part d'une pose et arrive à une pose, sur la même image : les enchaîner ne montre aucune coupure.
// Une boucle a son clip inversé : deux fois plus de variété, et un chemin court pour revenir à la pose.
// Au repos, elle garde une épaule relevée : « -g » celle de gauche à l'écran, « -d » celle de droite.
const LOOPS = {
  "repos-g": "attente-g", "repos-d": "attente-d", "repos-main-g": "autre-main-g", "repos-main-d": "autre-main-d",
  "reflexion-1": "cherche", "reflexion-2": "cherche", tape: "cherche", tient: "montre",
};
// Gestes du repos qui reviennent à leur pose : un coup d'œil à l'encadré de questions, le micro-casque ajusté par la main libre
// de son côté, et le calme de l'apparition. Joués seulement à leur tour, jamais au hasard.
const REST_LOOPS = { regard: "attente", micro: "autre-main", arrivee: "attente" };
// Elle relève une épaule en 2 s et garde cette posture ; le clip inversé la recentre.
const LEANS = {
  "penche-g": ["attente", "attente-g"], "penche-d": ["attente", "attente-d"],
  "penche-main-g": ["autre-main", "autre-main-g"], "penche-main-d": ["autre-main", "autre-main-d"],
};
export const CLIPS = {
  geste: ["attente", "cherche"],
  "geste-inverse": ["cherche", "attente"],
  montre: ["cherche", "montre"],
  // Après le Gantt, elle retourne sa tablette vers elle puis l'abaisse : le clip qui la rangeait d'un coup sautait.
  "montre-inverse": ["montre", "cherche"],
  "change-main": ["attente", "autre-main"],
  "change-main-inverse": ["autre-main", "attente"],
  ...Object.fromEntries(Object.entries(LEANS).flatMap(([name, [from, to]]) => [[name, [from, to]], [`${name}-inverse`, [to, from]]])),
  ...Object.fromEntries(Object.entries({ ...LOOPS, ...REST_LOOPS }).flatMap(([name, pose]) => [[name, [pose, pose]], [`${name}-inverse`, [pose, pose]]])),
};
// À l'apparition, deux secondes de calme, puis un coup d'œil à l'encadré de questions.
const FIRST_CLIP = "arrivee";
// Deux boucles de 5 s : avec la fin du geste qui la tourne, le Gantt reste visible une douzaine de secondes.
const SHOW_LOOPS = 2;
// Au repos, après chaque geste visible, elle relève une épaule, de l'autre côté que la fois précédente, garde cette posture une
// ou deux boucles de calme (5 à 10 s), puis se recentre avant le geste suivant : les épaules ne comptent pas comme un geste.
// Les gestes visibles viennent tour à tour ; le second de chaque main passe la tablette dans l'autre.
const CALM_LOOPS = [1, 2];
const REST_GESTURES = { attente: ["regard", "change-main"], "autre-main": ["micro", "change-main-inverse"] };
// Pose de la main pour chaque pose du repos, penchée ou non.
const HANDS = Object.fromEntries(Object.values(LEANS).flatMap(([hand, leaning]) => [[hand, hand], [leaning, hand]]));
const plain = (name) => name.replace(/-inverse$/, "");

// Pose suivante sur le plus court chemin : la tablette revient dans la main habituelle avant d'être levée, et se lit avant d'être montrée.
function stepToward(pose, wish) {
  const previous = { [pose]: pose };
  for (const queue = [pose]; queue.length && !(wish in previous);) {
    const current = queue.shift();
    for (const [from, to] of Object.values(CLIPS)) {
      if (from === current && !(to in previous)) {
        previous[to] = current;
        queue.push(to);
      }
    }
  }
  let next = wish;
  while (previous[next] !== pose) next = previous[next];
  return next;
}

// D'une pose à la pose voulue, sans jamais rejouer deux fois de suite le même clip.
export function nextClip(pose, wish, last, random = Math.random) {
  // La tablette levée pour une question, elle tape d'abord sa recherche.
  if (pose === "cherche" && last === "geste" && wish !== "attente") return "tape";
  const target = stepToward(pose, wish);
  const names = Object.keys(CLIPS).filter((name) => CLIPS[name][0] === pose && CLIPS[name][1] === target && !(plain(name) in REST_LOOPS));
  const choices = names.length > 1 ? names.filter((name) => name !== last) : names;
  return choices[Math.floor(random() * choices.length)];
}

// Une demande écourte la boucle d'attente en cours, sans couper le mouvement : au plus deux fois plus vite.
// Un geste garde sa vitesse : accéléré, il paraissait mécanique.
export const exitRate = (left) => Math.min(2, Math.max(1, left / 1.25));
export const twinOf = (name) => (name.endsWith("-inverse") ? name.slice(0, -8) : `${name}-inverse`);
export const isLoop = (name) => CLIPS[name][0] === CLIPS[name][1];
// Hors recherche, la tablette reste baissée ; le Gantt n'est montré qu'à la réponse, puis rangé.
export function idleWish(pose, wish, showLoops) {
  return pose === "montre" && wish === "montre" && showLoops < 0 ? "attente" : wish;
}
export const calmLoops = (random = Math.random) => CALM_LOOPS[0] + Math.floor(random() * (CALM_LOOPS[1] - CALM_LOOPS[0] + 1));
// Clip du repos à jouer après celui qui finit, ou "" pour une boucle de calme dans la posture penchée.
export function restGesture(rest, pose, wish, ended, random = Math.random) {
  const hand = HANDS[pose];
  if (wish !== pose || !hand) return "";
  if (pose !== hand) {
    if (!(plain(ended) in LOOPS) || --rest.calm > 0) return "";
    return `${Object.keys(LEANS).find((name) => LEANS[name][1] === pose)}-inverse`;
  }
  // Posture droite : après l'apparition ou en se recentrant, le geste visible suivant ; sinon elle penche.
  if (ended === FIRST_CLIP || (HANDS[CLIPS[ended][0]] === hand && CLIPS[ended][0] !== hand)) {
    const gestures = REST_GESTURES[hand];
    return gestures[rest.count++ % gestures.length];
  }
  rest.calm = calmLoops(random);
  const lean = Object.keys(LEANS).filter((name) => LEANS[name][0] === hand)[rest.side];
  rest.side = 1 - rest.side;
  return lean;
}

// La transparence VP9 manque à Safari : le coin de l'image y sort opaque.
function keepsTransparency(video) {
  const context = Object.assign(document.createElement("canvas"), { width: 1, height: 1 }).getContext("2d", { willReadFrequently: true });
  context.drawImage(video, 0, 0, 1, 1, 0, 0, 1, 1);
  return context.getImageData(0, 0, 1, 1).data[3] === 0;
}

// Sur un écran trop étroit, Lumi s'ouvre sans personnage ni média. Sans mouvement possible, l'image fixe reste à l'écran.
export function createEpiCharacterScene(stage, assetSource) {
  if (!stage.offsetWidth) return null;
  const still = stage.querySelector("img");
  still.src = assetSource("attente.webp");
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return null;
  const canvas = stage.querySelector("canvas");
  const context = canvas.getContext("2d");
  const state = { clip: "", pose: "attente", wish: "attente", thinking: false, stopped: false, switching: false, showLoops: 0 };
  // Après le calme de l'apparition, le coup d'œil ; la première posture relève l'épaule gauche de l'écran.
  const rest = { calm: 0, count: 0, side: 0 };
  const addresses = [];
  const videos = {};
  let frameId = 0;

  const stop = () => {
    state.stopped = true;
    cancelAnimationFrame(frameId);
    for (const video of Object.values(videos)) video.pause();
    for (const address of addresses) URL.revokeObjectURL(address);
    context.clearRect(0, 0, canvas.width, canvas.height);
    still.hidden = false;
  };
  // Une seule image à la fois sur le canevas : passer d'un clip à l'autre ne superpose jamais deux contours.
  const paint = () => {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(videos[state.clip], 0, 0, canvas.width, canvas.height);
    frameId = requestAnimationFrame(paint);
  };
  // Elle reprend sur ses pas quand c'est plus court : avant la moitié d'une boucle, ou pendant un geste qui l'éloigne de la pose voulue.
  const turnBack = async (video) => {
    const twin = videos[twinOf(state.clip)];
    const [from, to] = CLIPS[state.clip];
    const back = from === to ? video.currentTime < video.duration / 2 : stepToward(to, state.wish) === from;
    if (!back || !twin || twin.readyState < 2) return video;
    state.switching = true;
    video.pause();
    twin.currentTime = video.duration - video.currentTime;
    await new Promise((resolve) => { twin.onseeked = resolve; });
    state.clip = twinOf(state.clip);
    await twin.play();
    state.switching = false;
    video.currentTime = 0;
    return twin;
  };
  const hurry = async () => {
    const video = videos[state.clip];
    // Pendant une bascule, la demande attend : play() la reprend dès que le nouveau clip tourne.
    if (!video || state.switching) return;
    // Elle finit de taper : la réponse attend la fin du geste.
    if (CLIPS[state.clip][1] === state.wish || plain(state.clip) === "tape") {
      video.playbackRate = 1;
      return;
    }
    const current = await turnBack(video);
    current.playbackRate = isLoop(state.clip) ? exitRate(current.duration - current.currentTime) : 1;
  };

  // Rend le geste du repos à jouer, ou "".
  const chooseIdle = () => {
    if (state.pose === "montre" && state.wish === "montre") state.showLoops -= 1;
    state.wish = idleWish(state.pose, state.wish, state.showLoops);
    const gesture = restGesture(rest, state.pose, state.wish, state.clip);
    if (gesture) state.wish = CLIPS[gesture][1];
    return gesture;
  };
  const play = async () => {
    const before = videos[state.clip];
    let gesture = "";
    if (before) {
      state.pose = CLIPS[state.clip][1];
      if (!state.thinking) gesture = chooseIdle();
    }
    const name = before ? gesture || nextClip(state.pose, state.wish, state.clip) : FIRST_CLIP;
    await loaded[name];
    if (state.stopped) return;
    const video = videos[name];
    video.playbackRate = 1;
    // Le canevas passe au clip avant son démarrage : sa première image est celle qui finit le clip précédent, aucune n'est sautée.
    state.clip = name;
    state.switching = true;
    await video.play();
    state.switching = false;
    if (state.stopped) return;
    if (before) before.currentTime = 0;
    await hurry();
  };

  const load = async (name) => {
    const response = await fetch(assetSource(`${name}.webm`));
    if (!response.ok) throw new Error(name);
    const blob = await response.blob();
    if (state.stopped) return;
    const address = URL.createObjectURL(blob);
    addresses.push(address);
    const video = videos[name];
    video.src = address;
    await new Promise((resolve, reject) => {
      video.onloadeddata = resolve;
      video.onerror = reject;
    });
  };

  for (const name of Object.keys(CLIPS)) {
    videos[name] = Object.assign(document.createElement("video"), { muted: true, playsInline: true, preload: "auto", onended: () => play().catch(stop) });
  }
  // Un clip à la fois : les décoder tous ensemble ferait sauter des images au démarrage. Ordre d'emploi : l'apparition, le coup
  // d'œil et les postures de la main habituelle, puis la lecture et le changement de main ; en ligne, chacun est prêt à temps.
  let queue = Promise.resolve();
  const first = [FIRST_CLIP, ...Object.keys(CLIPS).filter((name) => CLIPS[name].every((pose) => HANDS[pose] === "attente")), "geste", "change-main"];
  const order = [...new Set([...first, ...Object.keys(CLIPS)])];
  const loaded = Object.fromEntries(order.map((name) => [name, queue = queue.then(() => load(name))]));
  queue.catch(stop);
  loaded[FIRST_CLIP].then(async () => {
    if (state.stopped) return;
    if (!keepsTransparency(videos[FIRST_CLIP])) {
      stop();
      return;
    }
    await play();
    if (state.stopped) return;
    paint();
    still.hidden = true;
  }).catch(stop);

  const want = (pose) => {
    state.wish = pose;
    hurry().catch(stop);
  };
  return {
    setThinking(thinking) {
      if (thinking === state.thinking) return;
      state.thinking = thinking;
      want(thinking ? "cherche" : "attente");
    },
    found() {
      state.thinking = false;
      state.showLoops = SHOW_LOOPS;
      want("montre");
    },
    destroy: stop,
  };
}
